// Iter-f3 — Brand Service Performance Dashboard (summary / branch cards).
//
// Lives under Brand workspace → Overview sub-menu, alongside Brand
// Overview and Cancellation. Mirrors their visual language but adds
// an A-vs-B date-range comparator so managers can judge any period
// against any other (this month vs same month last year, etc.).
//
// Branch cards show treatment + package totals for Period A and the
// percentage change versus Period B. Tapping a card opens the per-
// branch chart drill-down at /staff/brand/service-performance/:bid.
//
// No engine files are imported or edited — all heavy math stays on
// the server (`GET /reports/brand/service-performance/summary`).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { isOwner } from "@/lib/AuthEngine";
import { todayBali } from "@/lib/ReportingEngine";
import { InfoHint, InfoHintInline } from "@/components/common/InfoHint";
import { SERVICE_PERFORMANCE_TIPS } from "@/lib/reportTips";

// Iter-f7 — Default-range rule for every Overview dashboard:
//   Period A = first → last day of the PREVIOUS calendar month
//   Period B = first day of CURRENT month → today (Bali)
// Both ranges remain editable after mount; this is only the on-load default.
function previousMonthRange(todayISO) {
  const [y, m] = todayISO.split("-").map(Number);
  const prevYear  = m === 1 ? y - 1 : y;
  const prevMonth = m === 1 ? 12 : m - 1;
  const lastDay   = new Date(Date.UTC(prevYear, prevMonth, 0)).getUTCDate();
  const mm = String(prevMonth).padStart(2, "0");
  const dd = String(lastDay).padStart(2, "0");
  return [`${prevYear}-${mm}-01`, `${prevYear}-${mm}-${dd}`];
}
function currentMonthToToday(todayISO) {
  const [y, m] = todayISO.split("-");
  return [`${y}-${m}-01`, todayISO];
}

export default function BrandServicePerformanceDashboard() {
  const { staff } = useAuth();
  const nav = useNavigate();
  const today = todayBali();
  const [aFrom, setAFrom] = useState(() => previousMonthRange(today)[0]);
  const [aTo,   setATo]   = useState(() => previousMonthRange(today)[1]);
  const [bFrom, setBFrom] = useState(() => currentMonthToToday(today)[0]);
  const [bTo,   setBTo]   = useState(() => currentMonthToToday(today)[1]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  // Permission tick lives on Brand Report Permissions (iter-f3). Owner
  // always bypasses; everyone else — including Admin — must be ticked.
  const [tick, setTick] = useState(false);
  useEffect(() => {
    let cancelled = false;
    staffApi.get("/report-permissions/me", { params: { scope: "brand" } })
      .then((r) => {
        if (cancelled) return;
        const keys = new Set(r.data?.allowed_keys || []);
        setTick(keys.has("service_performance"));
      })
      .catch(() => { /* backend remains authority */ });
    return () => { cancelled = true; };
  }, []);
  const canSee = isOwner(staff) || tick;

  const params = useMemo(() => ({
    a_from: aFrom, a_to: aTo, b_from: bFrom, b_to: bTo,
  }), [aFrom, aTo, bFrom, bTo]);

  const load = useCallback(() => {
    if (!canSee) return;
    setLoading(true);
    staffApi.get("/reports/brand/service-performance/summary", { params })
      .then((r) => setSummary(r.data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [canSee, params]);
  useEffect(() => { load(); }, [load]);

  const branches = summary?.branches || [];

  return (
    <StaffShell context="brand">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold">Service Performance</h1>
          <p className="text-xs font-medium text-sky-600 mt-1" data-testid="brand-sp-label">
            Period A · {aFrom} → {aTo} &nbsp;vs&nbsp; Period B · {bFrom} → {bTo}
          </p>
        </div>
      </div>

      {!canSee && (
        <Card className="rounded-lg mb-6">
          <CardContent className="p-8 text-sm text-muted-foreground text-center">
            You don't have permission to view the Service Performance dashboard.
          </CardContent>
        </Card>
      )}

      {canSee && (
        <>
          <Card className="rounded-lg mb-4" data-testid="brand-sp-filters">
            <CardContent className="p-3 flex items-center gap-3 flex-wrap">
              <RangePicker label="Period A" from={aFrom} to={aTo} onFrom={setAFrom} onTo={setATo} testIdPrefix="brand-sp-a" />
              <span className="h-6 w-px bg-border mx-1" />
              <RangePicker label="Period B" from={bFrom} to={bTo} onFrom={setBFrom} onTo={setBTo} testIdPrefix="brand-sp-b" />
              <Button
                size="sm"
                variant="outline"
                onClick={load}
                disabled={loading}
                className="gap-1 ml-auto"
                data-testid="brand-sp-refresh"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </CardContent>
          </Card>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <TotalCard label="SINGLE TREATMENTS · A" value={summary?.grand_singles_a} tone="sky" loading={loading} testId="brand-sp-total-singles-a" tooltip={SERVICE_PERFORMANCE_TIPS.treatments} />
            <TotalCard label="SINGLE TREATMENTS · B" value={summary?.grand_singles_b} tone="muted" loading={loading} testId="brand-sp-total-singles-b" tooltip={SERVICE_PERFORMANCE_TIPS.treatments} />
            <TotalCard label="PACKAGES · A"           value={summary?.grand_packages_a} tone="amber" loading={loading} testId="brand-sp-total-packages-a" tooltip={SERVICE_PERFORMANCE_TIPS.treatments} />
            <TotalCard label="PACKAGES · B"           value={summary?.grand_packages_b} tone="muted" loading={loading} testId="brand-sp-total-packages-b" tooltip={SERVICE_PERFORMANCE_TIPS.treatments} />
          </div>

          <p className="tracking-overline text-[10px] text-muted-foreground mb-3">BY LOCATION</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {branches.map((b) => {
              const slug = (b.branch_name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
                <Card
                  key={b.branch_id}
                  onClick={() => nav(`/staff/brand/service-performance/${b.branch_id}?a_from=${aFrom}&a_to=${aTo}&b_from=${bFrom}&b_to=${bTo}`)}
                  className="rounded-lg cursor-pointer transition-shadow hover:shadow-md hover:ring-1 hover:ring-primary/30"
                  data-testid={`brand-sp-branch-${slug}`}
                >
                  <CardContent className="p-5">
                    <p className="font-medium">{b.branch_name}</p>
                    <div className="grid grid-cols-2 gap-3 mt-4">
                      <MetricCell
                        label="Singles"
                        value={b.singles_a}
                        delta={b.singles_delta_pct}
                        baseline={b.singles_b}
                        tone="sky"
                        testId={`brand-sp-branch-${slug}-singles`}
                        tooltip={SERVICE_PERFORMANCE_TIPS.treatments}
                      />
                      <MetricCell
                        label="Packages"
                        value={b.packages_a}
                        delta={b.packages_delta_pct}
                        baseline={b.packages_b}
                        tone="amber"
                        testId={`brand-sp-branch-${slug}-packages`}
                        tooltip={SERVICE_PERFORMANCE_TIPS.treatments}
                      />
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-border pt-3">
                      <SliceBlock heading="Top Singles" rows={b.top_singles} tone="sky" testId={`brand-sp-branch-${slug}-top-singles`} />
                      <SliceBlock heading="Top Packages" rows={b.top_packages} tone="amber" testId={`brand-sp-branch-${slug}-top-packages`} />
                      <SliceBlock heading="Bottom Singles" rows={b.bottom_singles} tone="muted" testId={`brand-sp-branch-${slug}-bottom-singles`} />
                      <SliceBlock heading="Bottom Packages" rows={b.bottom_packages} tone="muted" testId={`brand-sp-branch-${slug}-bottom-packages`} />
                    </div>
                    <p className="text-[10px] uppercase tracking-wider text-primary/80 mt-4">
                      Open branch details →
                    </p>
                  </CardContent>
                </Card>
              );
            })}
            {branches.length === 0 && !loading && (
              <Card className="rounded-lg sm:col-span-2 lg:col-span-3">
                <CardContent className="p-8 text-sm text-muted-foreground text-center">
                  No bookings in the selected Period A.
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}
    </StaffShell>
  );
}

function RangePicker({ label, from, to, onFrom, onTo, testIdPrefix }) {
  return (
    <div className="flex items-center gap-2">
      <span className="tracking-overline text-[10px] text-muted-foreground">{label}</span>
      <Input
        type="date"
        value={from}
        onChange={(e) => onFrom(e.target.value)}
        className="h-8 w-40 text-xs"
        data-testid={`${testIdPrefix}-from`}
      />
      <span className="text-xs text-muted-foreground">to</span>
      <Input
        type="date"
        value={to}
        onChange={(e) => onTo(e.target.value)}
        className="h-8 w-40 text-xs"
        data-testid={`${testIdPrefix}-to`}
      />
    </div>
  );
}

function TotalCard({ label, value, tone, loading, testId, tooltip }) {
  const toneColor = { sky: "text-sky-600", amber: "text-amber-600", muted: "text-muted-foreground" }[tone] || "text-foreground";
  return (
    <Card className="rounded-lg relative" data-testid={testId}>
      <InfoHint text={tooltip} testId={`${testId}-info`} />
      <CardContent className="p-5">
        <p className="tracking-overline text-[10px] text-muted-foreground pr-5">{label}</p>
        <p className={`text-3xl font-semibold mt-2 ${toneColor}`}>
          {loading ? "…" : (value ?? 0).toLocaleString("en-US")}
        </p>
      </CardContent>
    </Card>
  );
}

function MetricCell({ label, value, delta, baseline, tone, testId, tooltip }) {
  const toneColor = { sky: "text-sky-600", amber: "text-amber-600" }[tone] || "text-foreground";
  let DeltaIcon = Minus;
  let deltaClass = "text-muted-foreground";
  if (delta != null && delta > 0) { DeltaIcon = ArrowUpRight; deltaClass = "text-emerald-600"; }
  if (delta != null && delta < 0) { DeltaIcon = ArrowDownRight; deltaClass = "text-rose-600"; }
  const deltaLabel = delta == null ? "—" : `${delta > 0 ? "+" : ""}${delta}%`;
  return (
    <div data-testid={testId}>
      <p className="text-[11px] text-muted-foreground flex items-center gap-1">
        {label}
        <InfoHintInline text={tooltip} testId={`${testId}-info`} />
      </p>
      <p className={`text-xl font-semibold mt-0.5 ${toneColor}`}>{(value ?? 0).toLocaleString("en-US")}</p>
      <div className={`mt-1 inline-flex items-center gap-0.5 text-[11px] font-medium ${deltaClass}`} data-testid={`${testId}-delta`}>
        <DeltaIcon className="w-3 h-3" /> {deltaLabel}
        <span className="text-muted-foreground ml-1">(B · {(baseline ?? 0).toLocaleString("en-US")})</span>
      </div>
    </div>
  );
}

function SliceBlock({ heading, rows, tone, testId }) {
  // iter-f4 — Top 2 / Bottom 2 previews. We render only the rows the
  // backend sends (no empty-row padding) so a sparse branch doesn't
  // get fake "—" placeholders that make it look busier than it is.
  const toneColor = { sky: "text-sky-700", amber: "text-amber-700" }[tone] || "text-muted-foreground";
  return (
    <div data-testid={testId}>
      <p className="tracking-overline text-[9px] text-muted-foreground mb-1">{heading}</p>
      {(!rows || rows.length === 0) ? (
        <p className="text-[11px] text-muted-foreground/70 italic" data-testid={`${testId}-empty`}>None</p>
      ) : (
        <ul className="space-y-0.5">
          {rows.map((r, i) => (
            <li key={`${r.label}-${i}`} className="flex items-center justify-between gap-2 text-[11px]">
              <span className="truncate" title={r.label}>{r.label}</span>
              <span className={`font-semibold tabular-nums ${toneColor}`} data-testid={`${testId}-count-${i}`}>{r.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
